import { useState, useEffect, useCallback } from 'react';
import { Users, Mail, Shield, Trash2, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { Modal } from '../components/Modal';
import { getDateLocale } from '../i18n';
import type { AdminRole } from '@harbormaster/shared';

interface Member {
  id: string;
  name: string;
  email: string;
  role: AdminRole;
  marina_id: number | null;
  last_login_at: string | null;
}

interface PendingInvite {
  id: number;
  email: string;
  role: AdminRole;
  marina_id: number | null;
  expires_at: string;
}

// Role labels are now retrieved from i18n in render

const ROLE_COLORS: Record<AdminRole, string> = {
  platform_superadmin: 'bg-purple-100 text-purple-700',
  org_admin: 'bg-brand-100 text-brand-700',
  marina_admin: 'bg-blue-100 text-blue-700',
  marina_staff: 'bg-gray-100 text-gray-600',
};

export function TeamPage() {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const { error: toastError, success: toastSuccess } = useToast();
  const [members, setMembers] = useState<Member[]>([]);
  const [invites, setInvites] = useState<PendingInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [memberToRemove, setMemberToRemove] = useState<Member | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await api.getTeam();
      setMembers((data.members ?? []) as unknown as Member[]);
      setInvites((data.pending_invitations ?? []) as unknown as PendingInvite[]);
    } catch (err) {
      if (err instanceof ApiError) toastError(err.message);
    } finally {
      setLoading(false);
    }
  }, [toastError]);

  useEffect(() => { load(); }, [load]);

  const confirmRemove = async () => {
    const member = memberToRemove;
    if (!member) return;
    setRemovingId(member.id);
    try {
      await api.removeUser(member.id);
      toastSuccess(t('team.memberRemoved', { name: member.name }));
      setMemberToRemove(null);
      load();
    } catch (err) {
      toastError(err instanceof ApiError ? err.message : t('team.failedToRemove'));
    } finally {
      setRemovingId(null);
    }
  };

  const handleRevokeInvite = async (invite: PendingInvite) => {
    try {
      await api.revokeInvitation(invite.id);
      toastSuccess(t('team.inviteRevoked'));
      load();
    } catch (err) {
      toastError(err instanceof ApiError ? err.message : t('team.failedToRevoke'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">{t('team.title')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{t('team.subtitle')}</p>
        </div>
        <button
          onClick={() => setShowInvite(true)}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 transition-colors"
        >
          <Mail className="w-4 h-4" /> {t('team.inviteMember')}
        </button>
      </div>

      {/* Active members */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">{t('team.activeMembers', { count: members.length })}</span>
          </div>
        </div>
        {members.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">{t('team.noMembers')}</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {members.map(member => (
              <div key={member.id} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50/50">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-brand-100 flex items-center justify-center shrink-0">
                    <span className="text-xs font-bold text-brand-700">
                      {member.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{member.name}</p>
                    <p className="text-xs text-gray-400 truncate">{member.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ROLE_COLORS[member.role]}`}>
                    {t(`team.roles.${member.role}` as Parameters<typeof t>[0])}
                  </span>
                  {member.last_login_at && (
                    <span className="text-xs text-gray-400 hidden sm:block">
                      {new Date(member.last_login_at).toLocaleDateString(getDateLocale(), { day: 'numeric', month: 'short' })}
                    </span>
                  )}
                  {profile?.id !== member.id && member.role !== 'platform_superadmin' && (
                    <button
                      type="button"
                      onClick={() => setMemberToRemove(member)}
                      disabled={removingId === member.id}
                      className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pending invitations */}
      {invites.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-amber-500" />
              <span className="text-sm font-medium text-gray-700">{t('team.pendingInvitations', { count: invites.length })}</span>
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {invites.map(invite => (
              <div key={invite.id} className="flex items-center justify-between px-4 py-3">
                <div className="min-w-0">
                  <p className="text-sm text-gray-800 truncate">{invite.email}</p>
                  <p className="text-xs text-gray-400">
                    {t(`team.roles.${invite.role}` as Parameters<typeof t>[0])} · {t('team.expires')} {new Date(invite.expires_at).toLocaleDateString(getDateLocale(), { day: 'numeric', month: 'short' })}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-2">
                  <button
                    onClick={() => handleRevokeInvite(invite)}
                    className="text-xs text-red-500 hover:text-red-700 font-medium"
                  >
                    {t('team.revoke')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <InviteModal
        open={showInvite}
        onClose={() => setShowInvite(false)}
        onInvited={() => { setShowInvite(false); load(); }}
      />

      <Modal
        open={memberToRemove !== null}
        onClose={() => {
          if (removingId) return;
          setMemberToRemove(null);
        }}
        title={t('team.removeMemberTitle')}
        maxWidth="max-w-md"
      >
        {memberToRemove && (
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              {t('team.removeConfirm', { name: memberToRemove.name })}
            </p>
            <p className="text-xs text-gray-500">{t('team.removeMemberHint')}</p>
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                disabled={removingId !== null}
                onClick={() => setMemberToRemove(null)}
                className="flex-1 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                disabled={removingId !== null}
                onClick={() => void confirmRemove()}
                className="flex-1 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
              >
                {removingId ? t('common.deleting') : t('team.removeMemberSubmit')}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ── Invite Modal ─────────────────────────────────────────────

interface InviteForm {
  email: string;
  role: AdminRole;
  marina_id: string;
}

function InviteModal({ open, onClose, onInvited }: { open: boolean; onClose: () => void; onInvited: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { profile, isOrgAdmin } = useAuth();
  const [form, setForm] = useState<InviteForm>({ email: '', role: 'marina_staff', marina_id: '' });
  const [submitting, setSubmitting] = useState(false);
  const [marinas, setMarinas] = useState<{ id: number; name: string }[]>([]);

  useEffect(() => {
    if (open && isOrgAdmin) {
      api.getOrgMarinas()
        .then(data => {
          const list = ((data.marinas ?? []) as unknown as { id: number; name: string }[]);
          setMarinas(list);
        })
        .catch(() => {/* non-critical */});
    }
  }, [open, isOrgAdmin]);

  useEffect(() => {
    if (open) {
      const singleMarina =
        profile?.accessibleMarinaIds?.length === 1 ? String(profile.accessibleMarinaIds[0]) : '';
      setForm({ email: '', role: 'marina_staff', marina_id: singleMarina });
    }
  }, [open, profile?.accessibleMarinaIds]);

  const set = (k: keyof InviteForm, v: string) => setForm(f => ({ ...f, [k]: v }));

  const availableRoles: { value: AdminRole; label: string }[] = [
    ...(isOrgAdmin ? [{ value: 'org_admin' as AdminRole, label: t('team.roles.org_admin') }] : []),
    { value: 'marina_admin', label: t('team.roles.marina_admin') },
    { value: 'marina_staff', label: t('team.roles.marina_staff') },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.inviteUser({
        email: form.email.trim(),
        role: form.role,
        marina_id: form.marina_id ? parseInt(form.marina_id, 10) : undefined,
      });
      toast.success(t('team.invitationSent', { email: form.email }));
      onInvited();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('team.failedToSend'));
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/40';

  return (
    <Modal open={open} onClose={onClose} title={t('team.inviteTitle')} maxWidth="max-w-md">
      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('team.emailAddress')}</label>
          <input
            type="email"
            value={form.email}
            onChange={e => set('email', e.target.value)}
            required
            placeholder={t('team.emailPlaceholder')}
            className={inputClass}
            autoFocus
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('team.role')}</label>
          <select
            value={form.role}
            onChange={(e) => {
              const newRole = e.target.value as AdminRole;
              setForm((f) => {
                const next = { ...f, role: newRole };
                if (newRole === 'org_admin') {
                  next.marina_id = '';
                } else if (profile?.accessibleMarinaIds?.length === 1) {
                  next.marina_id = String(profile.accessibleMarinaIds[0]);
                }
                return next;
              });
            }}
            className={inputClass}
          >
            {availableRoles.map(r => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>
        {isOrgAdmin && marinas.length > 1 && form.role !== 'org_admin' && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('team.marina')}</label>
            <select
              value={form.marina_id}
              onChange={e => set('marina_id', e.target.value)}
              required
              className={inputClass}
            >
              <option value="">{t('team.selectMarina')}</option>
              {marinas.map(m => (
                <option key={m.id} value={String(m.id)}>{m.name}</option>
              ))}
            </select>
          </div>
        )}
        {isOrgAdmin && profile?.accessibleMarinaIds?.length === 1 && form.role !== 'org_admin' && (
          <p className="text-xs text-gray-500">{t('team.singleMarinaInviteHint')}</p>
        )}
        <div className="bg-blue-50 rounded-lg p-3 text-xs text-blue-700">
          <div className="flex items-center gap-1.5 font-medium mb-1">
            <Shield className="w-3.5 h-3.5" /> {t('team.permissions')}
          </div>
          {form.role === 'org_admin' && t('team.roleOrgAdmin')}
          {form.role === 'marina_admin' && t('team.roleMarinaAdmin')}
          {form.role === 'marina_staff' && t('team.roleMarinaStaff')}
        </div>
        <div className="flex gap-2 pt-1">
          <button type="button" onClick={onClose} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">{t('common.cancel')}</button>
          <button type="submit" disabled={submitting} className="flex-1 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 disabled:opacity-50">
            {submitting ? t('team.sending') : t('team.sendInvitation')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

